import type {
  ProductAttributeGroup,
  SyncProductDetailEntry,
} from '../types/product-overview.types';

/** Semantic tone for the availability badge — maps to the design-system
 *  success/warning/info tokens (neutral falls back to muted). */
export type AvailabilityTone = 'success' | 'warning' | 'info' | 'neutral';

/** Zero-width space that pollutes some feed descriptions — stripped before
 *  parsing so it never leaks into rendered copy or bullet splitting. */
const ZERO_WIDTH = /\u200B/g;

/**
 * Humanise a feed enum (`OUT_OF_STOCK`, `FEMALE`, `NEW`) to sentence case
 * ("Out of stock", "Female", "New"). Underscores/hyphens become spaces and
 * only the first letter is capitalised. Returns '' for empty input.
 */
export function humanizeEnum(raw: string | null | undefined): string {
  if (!raw) return '';
  const words = raw.trim().replace(/[_-]+/g, ' ').toLowerCase().replace(/\s+/g, ' ').trim();
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Resolve the availability label + semantic tone for the header badge.
 * `IN_STOCK` → success, `OUT_OF_STOCK` → warning, `PREORDER` → info,
 * anything else → neutral. Label is always humanised to sentence case.
 */
export function getAvailability(raw: string | null | undefined): {
  label: string;
  tone: AvailabilityTone;
} {
  const key = (raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  const tone: AvailabilityTone =
    key === 'IN_STOCK'
      ? 'success'
      : key === 'OUT_OF_STOCK'
        ? 'warning'
        : key === 'PREORDER'
          ? 'info'
          : 'neutral';
  return { label: humanizeEnum(raw), tone };
}

/**
 * Format a feed price string (`"74.00 USD"`) into a localised currency string
 * (`"$74.00"`). The feed carries the ISO-4217 code inline, so we parse
 * amount + currency and defer to `Intl.NumberFormat`. Returns '' when the
 * value is missing or unparseable (caller renders a "Not set" fallback).
 */
export function formatFeedPrice(raw: string | null | undefined): string {
  if (!raw) return '';
  const match = /^\s*([\d.,]+)\s*([A-Za-z]{3})\s*$/.exec(raw);
  const [, rawAmount, rawCurrency] = match ?? [];
  if (!rawAmount || !rawCurrency) return raw.trim();
  const amount = Number.parseFloat(rawAmount.replace(/,/g, ''));
  const currency = rawCurrency.toUpperCase();
  if (Number.isNaN(amount)) return raw.trim();
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    // Unknown/invalid currency code — fall back to the raw value rather than throw.
    return raw.trim();
  }
}

/** Structured description split out of the SEO-polluted feed string. */
export interface ParsedDescription {
  /** Human copy before the first ` - ` delimiter. */
  lead: string;
  /** ` - ` delimited feature items following the lead. */
  bullets: string[];
  /** Comma-separated keyword dump that follows the first `|`. */
  keywords: string[];
}

/**
 * Parse the feed `description`, which concatenates human copy with an SEO
 * keyword dump: `"Lead - feat one - feat two | kw1,kw2,kw3"`. Splits human
 * copy (before the first `|`) from keywords (after it), then breaks the human
 * side into a lead paragraph + ` - ` delimited bullets. Zero-width chars are
 * stripped throughout.
 */
export function parseProductDescription(raw: string | null | undefined): ParsedDescription {
  const clean = (raw ?? '').replace(ZERO_WIDTH, '').trim();
  if (!clean) return { lead: '', bullets: [], keywords: [] };

  const pipeAt = clean.indexOf('|');
  const humanPart = (pipeAt === -1 ? clean : clean.slice(0, pipeAt)).trim();
  const keywordPart = pipeAt === -1 ? '' : clean.slice(pipeAt + 1);

  const keywords = keywordPart
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);

  // Split the human copy on ` - `; the text before the first delimiter is the
  // lead paragraph, everything after is a feature bullet.
  const segments = humanPart
    .split(/\s+-\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const [lead = '', ...bullets] = segments;

  return { lead, bullets, keywords };
}

/**
 * Group a flat `product_details` list by `attribute_name`, deduping repeated
 * values while preserving first-seen order. The feed emits one entry per
 * attribute value, so grouping reconstructs each attribute's value set for
 * chip rendering.
 */
export function groupProductDetails(
  entries: SyncProductDetailEntry[] | null | undefined,
): ProductAttributeGroup[] {
  if (!entries?.length) return [];
  const groups = new Map<string, Set<string>>();
  for (const entry of entries) {
    const name = entry.attribute_name.trim();
    const value = entry.attribute_value.trim();
    if (!name || !value) continue;
    const set = groups.get(name) ?? new Set<string>();
    set.add(value);
    groups.set(name, set);
  }
  return [...groups.entries()].map(([name, values]) => ({ name, values: [...values] }));
}
